/**
 * Global JavaScript error capture.
 *
 * Hooks into three layers so any silent-failing JS error makes it to the
 * Sentinel /v1/frontend-errors endpoint (and from there to Cliq via the
 * backend's dedupe + classification rules):
 *
 *   1. app.config.errorHandler — Vue render/update/lifecycle errors
 *   2. window.addEventListener('error') — uncaught synchronous exceptions
 *      (including resource load errors, which we filter out)
 *   3. window.addEventListener('unhandledrejection') — uncaught promise rejects
 *
 * Uses native fetch (not axios) so a broken axios client can't create a loop
 * where the capture itself fails and re-enters itself.
 */
import type { App } from 'vue'
import { isNavigationFailure } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { notifyError } from '../services/toastBridge'

const ERROR_REPORT_URL = import.meta.env.DEV
  ? '/api/v1/frontend-errors'
  : 'https://api.claimshieldpro.com/api/v1/frontend-errors'

type ErrorType = 'js_exception' | 'unhandled_rejection'

// Simple client-side dedupe so a single broken render doesn't POST 60 times.
const recentReports = new Map<string, number>()
const CLIENT_DEDUPE_MS = 10_000

function shouldReport(key: string): boolean {
  const now = Date.now()
  const last = recentReports.get(key)
  if (last && now - last < CLIENT_DEDUPE_MS) return false
  recentReports.set(key, now)
  if (recentReports.size > 100) {
    for (const [k, ts] of recentReports) {
      if (now - ts > CLIENT_DEDUPE_MS * 2) recentReports.delete(k)
    }
  }
  return true
}

function getUserEmail(): string | null {
  try {
    return useAuthStore().user?.email ?? null
  } catch {
    // Pinia might not be mounted yet during early boot
    return null
  }
}

// "Maximum call stack size exceeded" with zero first-party frames in the stack
// is the signature of a browser extension (password manager, autofill, content
// blocker) recursively walking the page and crashing on its own internals. We
// can't fix the extension, and these reports drown out real errors. Drop them.
const FIRST_PARTY_STACK_RE = /claimshieldpro\.com|\/apply\/assets\/|\/portal\/assets\/|\/ops\/assets\//

function isExtensionOnlyStackOverflow(message: string, stack: string | undefined): boolean {
  if (!/Maximum call stack size exceeded/i.test(message)) return false
  return !stack || !FIRST_PARTY_STACK_RE.test(stack)
}

// Vue Router throws these by design when:
//   - a user clicks a link to the current route (DUPLICATED)
//   - a navigation is interrupted by a newer one (CANCELLED)
//   - a guard rejects (ABORTED)
// They self-recover and are not actionable. Catching them here keeps
// #sentinel-alerts focused on real bugs.
function isExpectedRouterNavigationError(err: unknown): boolean {
  try {
    return isNavigationFailure(err)
  } catch {
    return false
  }
}

// Axios attaches `isAxiosError = true` to every error it raises. The axios
// response interceptor (shared/services/api.ts) already reports these via the
// `error_type=api` path with full endpoint/method/status context, where the
// classifier can decide whether they're actionable. If the consumer code
// didn't `.catch()` and the rejection bubbles up here, re-reporting it as
// `unhandled_rejection` (which always alerts) would just spam Cliq with a
// less-informative duplicate. Drop them.
function isAxiosErrorLike(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { isAxiosError?: unknown }).isAxiosError === true
}

// Generic browser fetch-failure messages (Safari "Load failed", Chrome
// "Failed to fetch", Firefox "NetworkError when attempting to fetch resource")
// fired through unhandledrejection are almost always client-side connectivity
// drops — wifi bounce, lid closed, mobile signal, navigation aborting an
// in-flight request. Without a first-party stack frame, there's no actionable
// bug on our side. The axios path's `_NETWORK_ERROR_ALERT_ENDPOINTS` whitelist
// already covers the cases where a network drop on a critical endpoint matters.
const NETWORK_FAILURE_MESSAGE_RE = /^(load failed|failed to fetch|networkerror when attempting to fetch resource|network ?error)\.?$/i

function isGenericNetworkFailure(message: string, stack: string | undefined): boolean {
  if (!NETWORK_FAILURE_MESSAGE_RE.test(message.trim())) return false
  return !stack || !FIRST_PARTY_STACK_RE.test(stack)
}

function reportJsError(
  errorType: ErrorType,
  message: string,
  stack?: string,
): void {
  try {
    if (isExtensionOnlyStackOverflow(message, stack)) return
    const key = `${errorType}:${message.slice(0, 200)}`
    if (!shouldReport(key)) return

    fetch(ERROR_REPORT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        error_message: message.slice(0, 2000),
        error_type: errorType,
        stack_trace: stack ? stack.slice(0, 4000) : null,
        http_status: null,
        failed_endpoint: null,
        http_method: null,
        user_email: getUserEmail(),
        page_path: window.location.pathname,
        timestamp: new Date().toISOString(),
      }),
      keepalive: true,
    })
    // fire-and-forget
  } catch {
    // swallow — capture must never throw
  }
}

function formatError(err: unknown): { message: string; stack?: string } {
  if (err instanceof Error) {
    return { message: err.message || err.name, stack: err.stack }
  }
  if (typeof err === 'string') return { message: err }
  try {
    return { message: JSON.stringify(err).slice(0, 500) }
  } catch {
    return { message: String(err) }
  }
}

/**
 * Install global error capture. Call once per app from main.ts, after the
 * Pinia plugin is registered so getUserEmail() can read the auth store.
 */
export function installErrorCapture(app: App): void {
  // Layer 1 — Vue component errors
  app.config.errorHandler = (err, _instance, info) => {
    // Vue Router navigation failures are expected and self-recover — silence.
    if (isExpectedRouterNavigationError(err)) {
      console.debug('[router nav skip]', err)
      return
    }
    // Axios errors are reported by the axios interceptor with full context
    // (endpoint/method/status). When an axios rejection propagates into a
    // Vue render or computed, Vue's errorHandler fires a second alert that's
    // strictly noisier — same skip rule as Layer 3 (unhandledrejection).
    if (isAxiosErrorLike(err)) {
      console.debug('[axios already-reported skip]', err)
      return
    }
    const { message, stack } = formatError(err)
    // Generic browser fetch-failure messages with no first-party stack are
    // client-side connectivity blips, not bugs — same skip rule as Layer 3.
    if (isGenericNetworkFailure(message, stack)) {
      console.debug('[network-failure skip]', message)
      return
    }
    console.error('[Vue errorHandler]', info, err)
    reportJsError('js_exception', `[vue:${info}] ${message}`, stack)
    notifyError(
      'Something went wrong on this page',
      'Try refreshing. If it keeps happening, let us know.',
    )
  }

  // Layer 2 — uncaught synchronous JS errors
  window.addEventListener('error', (event) => {
    // Resource load failures (img/script/link) also fire 'error' but
    // event.error is null — filter those out, they're handled by the browser.
    if (!event.error && !event.message) return
    // Cross-origin script error: browser redacts to `message="Script error."`
    // with no error object, no filename, no lineno, no stack. All our bundles
    // are same-origin, so this is always a browser-extension content script
    // (password manager, autofill, ad blocker) throwing in the page context.
    // Nothing actionable.
    if (
      !event.error &&
      event.message === 'Script error.' &&
      !event.filename
    ) return
    const { message, stack } = formatError(event.error ?? event.message)
    reportJsError(
      'js_exception',
      `[window] ${message} @ ${event.filename ?? 'unknown'}:${event.lineno ?? 0}`,
      stack,
    )
  })

  // Layer 3 — unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    // Router navigation failures sometimes propagate as promise rejections
    // when triggered programmatically (router.push from a button) — same
    // expected-and-noisy story.
    if (isExpectedRouterNavigationError(event.reason)) {
      console.debug('[router nav skip]', event.reason)
      return
    }
    // Axios errors are reported by the axios interceptor with full context
    // (endpoint/method/status). Re-reporting here as unhandled_rejection
    // would fire a less-informative duplicate alert.
    if (isAxiosErrorLike(event.reason)) {
      console.debug('[axios already-reported skip]', event.reason)
      return
    }
    const { message, stack } = formatError(event.reason)
    // Generic browser fetch-failure messages (Safari "Load failed", etc.)
    // with no first-party stack are client-side connectivity blips, not bugs.
    if (isGenericNetworkFailure(message, stack)) {
      console.debug('[network-failure skip]', message)
      return
    }
    reportJsError('unhandled_rejection', message, stack)
  })
}
